import { useMutation, useQueryClient } from '@tanstack/react-query';
import { startOfWeek, endOfWeek } from 'date-fns';
import { toast } from 'sonner';
import { scheduleKeys } from './queryKeys';
import { useSchedulingMutations, mapShiftRpcError } from '@/hooks/useSchedulingMutations';

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
}

interface EditShiftArgs {
  shiftId: string;
  profileId: string;
  start: Date;
  end: Date;
  note: string | null;
}

export const useEditShift = (
  companyId: string | undefined,
  currentWeek: Date,
  employees: Profile[]
) => {
  const queryClient = useQueryClient();
  const weekStartISO = startOfWeek(currentWeek).toISOString();
  const weekEndISO = endOfWeek(currentWeek).toISOString();
  const { editShift: editShiftMutation } = useSchedulingMutations();
  
  return useMutation({
    mutationFn: async (args: EditShiftArgs) => {
      const updatedShiftData = await editShiftMutation(args);
      return { updatedShiftData, args };
    },
    
    onMutate: async (args) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO)
      });
      
      // Snapshot previous value
      const previousShifts = queryClient.getQueryData(
        scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO)
      );
      
      // Create optimistic update
      const selectedProfile = employees.find(p => p.id === args.profileId);
      const optimisticShift = {
        id: args.shiftId,
        profile_id: args.profileId,
        start_time: args.start.toISOString(),
        end_time: args.end.toISOString(),
        note: args.note,
        status: 'active' as const,
        employee_name: selectedProfile 
          ? `${selectedProfile.first_name} ${selectedProfile.last_name}`.trim()
          : '—',
        company_id: companyId
      };
      
      // Optimistically update cache
      queryClient.setQueryData(
        scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO),
        (old: any[]) => (old || []).map(shift => 
          shift.id === args.shiftId ? optimisticShift : shift
        )
      );
      
      return { previousShifts, optimisticShift };
    },
    
    onSuccess: ({ updatedShiftData, args }, _, context) => {
      // Update with real data from server
      const selectedProfile = employees.find(p => p.id === args.profileId);
      const enrichedShift = {
        ...updatedShiftData,
        status: (updatedShiftData.status as 'active' | 'open') || 'active',
        employee_name: selectedProfile 
          ? `${selectedProfile.first_name} ${selectedProfile.last_name}`.trim()
          : '—'
      };
      
      queryClient.setQueryData(
        scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO),
        (old: any[]) => (old || []).map(shift => 
          shift.id === args.shiftId ? enrichedShift : shift
        )
      );
      
      toast.success('Shift updated successfully');
    },
    
    onError: (error: any, args, context) => {
      // Rollback optimistic update
      if (context?.previousShifts) {
        queryClient.setQueryData(
          scheduleKeys.companyShifts(companyId || '', weekStartISO, weekEndISO),
          context.previousShifts
        );
      }
      
      const errorMessage = mapShiftRpcError(error.message);
      toast.error(errorMessage || 'Failed to update shift');
    },
    
    onSettled: () => {
      // Always refetch to ensure consistency across all schedule views
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.all
      });
    }
  });
};
